import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CpForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverMsg, setServerMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/client-portal/auth/forgot-password`,
                                 { email: email.trim().toLowerCase() });
      setServerMsg(r.data?.message || "");
      setSent(true);
    } catch (err) {
      // Even on error we show the generic success — never leak account-existence.
      setServerMsg(
        "If an account with that email exists, we've sent a password reset link. "
        + "Check your inbox (and spam folder) — the link is valid for 30 minutes.",
      );
      setSent(true);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex md:w-1/2 bg-[#0a2540] text-white items-center justify-center p-12">
        <div className="max-w-md">
          <h1 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
            Reset access<br />in a minute.
          </h1>
          <p className="text-sm text-slate-300 leading-relaxed">
            We&apos;ll email you a secure, single-use link to reset your password.
            The link expires in 30 minutes.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <Link
            to="/client-portal/login"
            className="text-xs text-slate-500 hover:text-[#0a2540] inline-flex items-center gap-1 mb-6"
          >
            <ArrowLeft size={11} /> Back to sign in
          </Link>
          {sent ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="cp-forgot-success">
              <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-3" />
              <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: "Outfit" }}>
                Check your inbox
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                {serverMsg}
              </p>
              <p className="text-[11px] text-slate-400 mt-4">
                Didn&apos;t get it? Wait a minute, then{" "}
                <button
                  onClick={() => { setSent(false); setServerMsg(""); }}
                  className="text-[#0a2540] underline"
                  data-testid="cp-forgot-retry"
                >
                  try again
                </button>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h2 className="text-xs tracking-[0.18em] uppercase text-slate-500 font-semibold">Forgot password</h2>
                <h1 className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                  Reset your password
                </h1>
                <p className="text-xs text-slate-500 mt-2">
                  Enter the email address you use to sign in. We&apos;ll send you a link to choose a new password.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cp-fp-email">Email</Label>
                <Input
                  id="cp-fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="cp-forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !email}
                className="w-full bg-[#0a2540] hover:bg-[#0a2540]/90"
                data-testid="cp-forgot-submit"
              >
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Mail size={16} className="mr-2" />}
                Send reset link
              </Button>
              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                For security we always show the same confirmation regardless of whether the email is registered.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
